import type { FC, FocusEventHandler, MouseEventHandler, PropsWithChildren, ReactNode } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { useOnClickOutside } from 'usehooks-ts';

import { useIsPointerDevice } from '@motorway/mw-highway-code/hooks';

import { applyCypressData } from 'Utilities/helpers';

import styles from './Tooltip.module.scss';

export type TooltipProps = Prettify<
	Omit<
		PropsWithChildren &
			RadixTooltip.TooltipProps &
			RadixTooltip.TooltipContentProps &
			RadixTooltip.TooltipProviderProps,
		'content'
	> & { content: ReactNode; onFocus?: FocusEventHandler<HTMLButtonElement>; openOnTap?: boolean } & {
		onClickedOutside?: () => void;
		onMouseEnter?: MouseEventHandler<HTMLButtonElement>;
	}
>;

const Tooltip: FC<TooltipProps> = ({
	children,
	content,
	defaultOpen,
	delayDuration = 0,
	onClickedOutside,
	onFocus,
	open,
	openOnTap = false,
	side = 'bottom',
	sideOffset = 4,
	skipDelayDuration,
	...props
}) => {
	const triggerRef = useRef(null);
	const tooltipRef = useRef(null);

	const [tapOpen, setTapOpen] = useState<boolean | undefined>(open || undefined);

	const isPointerDevice = useIsPointerDevice();

	const onPointerUp = useCallback(() => {
		if (!isPointerDevice || !openOnTap) {
			return;
		}

		setTapOpen((prev) => !prev);
	}, [isPointerDevice, openOnTap]);

	useOnClickOutside([triggerRef, tooltipRef], () => {
		onClickedOutside?.();

		if (!isPointerDevice || !openOnTap) {
			return;
		}

		setTapOpen(false);
	});

	return (
		<RadixTooltip.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration}>
			<RadixTooltip.Root defaultOpen={defaultOpen} onOpenChange={props.onOpenChange} open={tapOpen ?? open}>
				<RadixTooltip.Trigger
					ref={triggerRef}
					asChild
					onFocus={onFocus}
					onMouseEnter={props.onMouseEnter}
					onPointerUp={onPointerUp}
				>
					{children}
				</RadixTooltip.Trigger>
				<RadixTooltip.Portal>
					<RadixTooltip.Content
						ref={tooltipRef}
						className={styles.content}
						side={side}
						sideOffset={sideOffset}
						{...applyCypressData('tooltip')}
					>
						{content}
						<RadixTooltip.Arrow className={styles.arrow} />
					</RadixTooltip.Content>
				</RadixTooltip.Portal>
			</RadixTooltip.Root>
		</RadixTooltip.Provider>
	);
};

export default Tooltip;
